<template>
  <BaseLayout title="Manage Semesters">
    <div class="container my-4">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1 class="h3 mb-0">Manage Semesters</h1>
        <button class="btn btn-primary" @click="openCreate">
          <i class="bi bi-plus-lg me-1"></i> New semester
        </button>
      </div>

      <div class="form-check form-switch mb-3">
        <input class="form-check-input" type="checkbox" id="showArchived" v-model="showArchived" @change="loadSemesters">
        <label class="form-check-label" for="showArchived">Show archived semesters</label>
      </div>

      <div v-if="loading" class="text-center my-5">
        <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading…</span></div>
      </div>

      <table v-else class="table table-hover align-middle">
        <thead>
          <tr>
            <th>Name</th>
            <th>Start</th>
            <th>End</th>
            <th>Status</th>
            <th class="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="semester in semesters" :key="semester.id" :class="{ 'text-muted': semester.archived }">
            <td>{{ semester.name }}</td>
            <td>{{ formatDate(semester.start_date) }}</td>
            <td>{{ formatDate(semester.end_date) }}</td>
            <td>
              <span v-if="semester.id === currentSemesterId" class="badge bg-success me-1">Current</span>
              <span v-if="semester.archived" class="badge bg-secondary">Archived</span>
              <span v-else-if="semester.id !== currentSemesterId" class="badge bg-light text-dark">Active</span>
            </td>
            <td class="text-end">
              <button v-if="!semester.archived" class="btn btn-sm btn-outline-primary me-2" @click="openEdit(semester)">
                <i class="bi bi-pencil"></i> Edit
              </button>
              <button v-if="!semester.archived" class="btn btn-sm btn-outline-warning"
                :disabled="semester.id === currentSemesterId"
                :title="semester.id === currentSemesterId ? 'Cannot archive the current semester' : 'Archive'"
                @click="archive(semester)">
                <i class="bi bi-archive"></i> Archive
              </button>
              <button v-else class="btn btn-sm btn-outline-success" @click="unarchive(semester)">
                <i class="bi bi-arrow-counterclockwise"></i> Unarchive
              </button>
            </td>
          </tr>
          <tr v-if="semesters.length === 0">
            <td colspan="5" class="text-center text-muted py-4">No semesters found.</td>
          </tr>
        </tbody>
      </table>

      <p class="text-muted small">
        Archiving hides a semester but keeps all its data (restore it any time via <em>Show archived semesters</em>).
        To end the current semester and start fresh — wiping workers &amp; datasets — use
        <router-link to="/course_actions">Course Actions → End current and prepare new semester</router-link>.
      </p>
    </div>

    <!-- Progress overlay shown while a create-with-copy job runs (polled). -->
    <div v-if="creatingWithCopy" class="semester-copy-overlay d-flex flex-column align-items-center justify-content-center text-center">
      <div class="spinner-border text-primary mb-3" role="status" style="width: 3rem; height: 3rem;">
        <span class="visually-hidden">Working…</span>
      </div>
      <h2 class="h5">Creating semester and copying resources…</h2>
      <div class="progress mb-2" style="width: 320px; max-width: 80vw; height: 1.25rem;">
        <div class="progress-bar" role="progressbar"
          :style="{ width: copyProgress.total ? (copyProgress.done / copyProgress.total * 100) + '%' : '100%' }"
          :class="{ 'progress-bar-striped progress-bar-animated': !copyProgress.total }"
          :aria-valuenow="copyProgress.done" aria-valuemin="0" :aria-valuemax="copyProgress.total">
        </div>
      </div>
      <p class="text-muted small mb-1">
        {{ copyProgress.total ? copyProgress.done + ' / ' + copyProgress.total + ' items copied' : 'Starting…' }}
      </p>
      <p class="text-muted" style="max-width: 600px;">
        Copying global files and homeworks into the new semester. This can take a while — please keep this page open.
      </p>
    </div>

    <SemesterFormModal ref="semesterFormModal" @submitForm="handleSubmit" />
    <StemErrorModal ref="stemErrorModal" />
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/components/BaseLayout.vue';
import SemesterFormModal from '@/components/SemesterFormModal.vue';
import StemErrorModal from '@/components/StemErrorModal.vue';
import SemesterController from '@/controllers/SemesterController';
import Semester from '@/models/semester';
import { mapGetters } from 'vuex';

export default {
  name: 'ManageSemestersView',
  components: { BaseLayout, SemesterFormModal, StemErrorModal },
  data() {
    return {
      semesters: [],
      showArchived: true,
      loading: false,
      creatingWithCopy: false,
      copyProgress: { done: 0, total: 0 },
      copyPollTimer: null,
    };
  },
  beforeUnmount() {
    this.clearCopyPoll();
  },
  computed: {
    ...mapGetters(['isTeacher']),
    // Mirror backend get_current(): the newest non-archived semester by start_date.
    currentSemesterId() {
      const active = this.semesters.filter(s => !s.archived);
      if (active.length === 0) return null;
      return active.reduce((latest, s) =>
        new Date(s.start_date) > new Date(latest.start_date) ? s : latest
      ).id;
    },
  },
  async created() {
    if (!this.isTeacher) {
      this.$store.dispatch('triggerAlert', {
        type: 'error',
        title: 'Error',
        message: 'You are not authorized to access this page.',
      });
      this.$router.push('/');
      return;
    }
    await this.loadSemesters();
  },
  methods: {
    async loadSemesters() {
      try {
        this.loading = true;
        this.semesters = await SemesterController.fetchSemesters(this.showArchived);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.$refs.semesterFormModal.show(null);
    },
    openEdit(semester) {
      this.$refs.semesterFormModal.show(semester);
    },
    async handleSubmit(payload) {
      if (payload.id) {
        await this.updateSemester(payload);
      } else {
        await this.createSemester(payload);
      }
    },
    async createSemester(payload) {
      const withCopy = payload.copy_resources_from_last_semester === true;
      try {
        if (withCopy) {
          this.creatingWithCopy = true;
          this.copyProgress = { done: 0, total: 0 };
        }
        const { copyJobId } = await SemesterController.createSemester({
          name: payload.name,
          start_date: payload.start_date,
          end_date: payload.end_date,
          copy_resources_from_last_semester: withCopy,
          archived: false,
        });
        if (copyJobId) {
          // A background copy is running — poll it for progress, finish when done.
          this.trackCopyJob(copyJobId);
          return;
        }
        await this.afterCreate('Semester created.');
      } catch (error) {
        this.creatingWithCopy = false;
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async afterCreate(message) {
      // The new semester may have become "current"; refresh the token so the teacher's session picks it up.
      await this.$store.dispatch('refreshToken');
      this.$store.dispatch('triggerAlert', { type: 'success', title: 'Success', message });
      await this.loadSemesters();
    },
    trackCopyJob(jobId) {
      const poll = async () => {
        let status;
        try {
          status = await SemesterController.getCopyStatus(jobId);
        } catch (error) {
          // Job vanished (e.g. a backend restart) or a network error — stop and surface it.
          this.creatingWithCopy = false;
          this.clearCopyPoll();
          this.$refs.stemErrorModal.show('Copy status unavailable', error.message);
          await this.loadSemesters();
          return;
        }
        this.copyProgress = { done: status.done, total: status.total };
        if (status.status === 'done') {
          this.creatingWithCopy = false;
          this.clearCopyPoll();
          await this.afterCreate('Semester created and resources copied.');
          return;
        }
        if (status.status === 'error') {
          this.creatingWithCopy = false;
          this.clearCopyPoll();
          this.$refs.stemErrorModal.show('Copy failed', status.error || 'Resource copy failed.');
          await this.loadSemesters();
          return;
        }
        this.copyPollTimer = setTimeout(poll, 1500);
      };
      poll();
    },
    clearCopyPoll() {
      if (this.copyPollTimer) {
        clearTimeout(this.copyPollTimer);
        this.copyPollTimer = null;
      }
    },
    async updateSemester(payload) {
      try {
        await SemesterController.updateSemester(payload.id, {
          name: payload.name,
          start_date: payload.start_date,
          end_date: payload.end_date,
          copy_resources_from_last_semester: false,
          archived: false,
        });
        this.$store.dispatch('triggerAlert', { type: 'success', title: 'Success', message: 'Semester updated.' });
        await this.loadSemesters();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async archive(semester) {
      // Client-side mirror of the backend guard, for immediate feedback.
      if (semester.id === this.currentSemesterId) {
        this.$refs.stemErrorModal.show('Cannot archive',
          'You cannot archive the current semester. Create or roll over to a newer semester first.');
        return;
      }
      if (!window.confirm(`Archive semester "${semester.name}"? Its data is kept and can be restored later.`)) return;
      try {
        await SemesterController.archiveSemester(semester.id);
        this.$store.dispatch('triggerAlert', { type: 'success', title: 'Success', message: 'Semester archived.' });
        await this.loadSemesters();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async unarchive(semester) {
      if (!window.confirm(`Restore semester "${semester.name}"?`)) return;
      try {
        await SemesterController.unarchiveSemester(semester.id);
        this.$store.dispatch('triggerAlert', { type: 'success', title: 'Success', message: 'Semester restored.' });
        await this.loadSemesters();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    formatDate(value) {
      return Semester.formatDate(value);
    },
  },
};
</script>

<style scoped>
.semester-copy-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.9);
  z-index: 1080;
}
</style>
